import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Heading from "@components/layout/Heading";
import SelectSingleLevel1WRSTable from "@components/table/SelectSingleLevel1WRSTable";
import { useSubscriberSyncUpload } from "@context/subscriberSyncUpload/Provider";

const SelectLevel1WRS = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: wizardState, dispatch } = useSubscriberSyncUpload();
  const { state: locationState } = useLocation();
  const params = useParams();

  const routeLevel1ResourceId =
    (locationState as { level1Resource?: { id?: string } } | null)
      ?.level1Resource?.id ?? params.id;

  // Scheme-route entry: pin the scheme and skip to step 1 (Upload). The reducer's
  // select-scheme moves the wizard from select-scheme → upload-file, and the parent
  // page's effect mirrors that into StepProvider, so we don't need to call stepHelpers
  // here.
  React.useEffect(() => {
    if (routeLevel1ResourceId && wizardState.step === "select-scheme") {
      dispatch({
        type: "select-scheme",
        level1ResourceId: routeLevel1ResourceId,
      });
    }
  }, [routeLevel1ResourceId, wizardState.step, dispatch]);

  // Local picker state — selecting a row only updates the radio, never advances. Seed from wizard
  // state so returning to step 0 via Previous reflects the previously picked scheme.
  const [pendingSchemeId, setPendingSchemeId] = React.useState<string | undefined>(
    wizardState.level1ResourceId ?? undefined,
  );

  if (routeLevel1ResourceId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 p-6 grow justify-between">
      <Heading light>{t("subscriber_sync_upload.step.select_scheme")}</Heading>
      <SelectSingleLevel1WRSTable
        onSelect={(selected: any) => setPendingSchemeId(selected?.id)}
        selected={pendingSchemeId ? { id: pendingSchemeId } : undefined}
      />

      <footer className="flex gap-4 -mx-6 mt-2 p-6 pb-0 border-t border-gray-200">
        <button
          type="button"
          className="btn-primary"
          disabled={!pendingSchemeId}
          onClick={() => {
            if (!pendingSchemeId) return;
            dispatch({ type: "select-scheme", level1ResourceId: pendingSchemeId });
          }}
        >
          {t("common.next_step")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={() => {
            dispatch({ type: "reset" });
            navigate("/polestar/subscribers/sync_uploads");
          }}
        >
          {t("common.cancel")}
        </button>
      </footer>
    </div>
  );
};

export default SelectLevel1WRS;
